import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import useSiteConfig from "@/lib/useSiteConfig";
import FindYourStayCTA from "@/components/common/FindYourStayCTA";

// Spell out small property counts as words ("Ten stays") for editorial copy;
// fall back to the digit + "+" suffix for larger numbers. Keeps About / Guest
// Love / Shoots all reading the same number from /api/site-config.
const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const spellOut = (n) => (n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n][0].toUpperCase() + NUMBER_WORDS[n].slice(1) : `${n}`);

export default function About() {
  const cfg = useSiteConfig();
  const propCount = Math.max(1, Number(cfg.active_properties_count || 0));
  const [hero, setHero] = useState("");
  const [still, setStill] = useState("");
  useEffect(() => {
    api.get("/properties").then(({ data }) => {
      const sombre = data.find((p) => p.slug === "sombre");
      const doodle = data.find((p) => p.slug === "doodle");
      setHero(sombre?.hero_image || data[0]?.hero_image || "");
      setStill(doodle?.hero_image || data[1]?.hero_image || "");
    });
  }, []);

  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About · Vyagram Experiences</title>
        <meta name="description" content="Vyagram is a small team of architects and hospitality veterans building experience-led stays in Delhi and Gurugram." />
      </Helmet>

      <section className="relative isolate overflow-hidden">
        {hero && <img src={hero} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 to-noir/70" />
        <div className="relative mx-auto max-w-4xl px-4 py-24 sm:py-36">
          <p className="font-accent text-base text-gold uppercase tracking-[0.25em]">About Vyagram</p>
          <h1 className="mt-4 font-display text-4xl text-parchment sm:text-6xl">
            Designed by architects. Run by hospitality people.
          </h1>
          <p className="mt-5 max-w-2xl font-editorial text-lg text-parchment/85 sm:text-xl">
            {spellOut(propCount)} stays across South Delhi and Gurugram &mdash; every one built, staffed and
            housekept by the same people you&rsquo;ll message when you&rsquo;re on the way in.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-10">
        <p className="vyg-eyebrow">Who we are</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl">A small team with a strong point of view.</h2>
        <p className="editorial-body mt-5 text-xl">
          Vyagram is run by a group of architects and hospitality professionals who were tired of
          short stays that felt interchangeable. Between us we have <span className="font-medium text-forest">15+ years in hospitality</span>
          {" "}and <span className="font-medium text-forest">10+ years in architecture &amp; interior design</span>,
          and every stay in the portfolio is the product of both disciplines sitting in the same room.
        </p>
        <p className="editorial-body mt-5 text-xl">
          We call the approach <em>experience-led design</em>. Every home is built around a feeling &mdash;
          the moody calm of Japandi at Sombre, the neon theatre of Cyberpunk, the quiet gothic of
          Speakeasy &mdash; and then dressed for real life, not just photos. Nothing is templated. Everything
          is tested by actually living in it.
        </p>

        <p className="vyg-eyebrow mt-14">How we&rsquo;ve grown</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl">The highest repeat-booking rate we know of.</h2>
        <p className="editorial-body mt-5 text-xl">
          Most of our bookings come from guests returning, or from someone they told. We read every
          review, every WhatsApp, every post-stay note &mdash; and the next iteration of each home, and
          each new property, is genuinely shaped by what guests tell us. It&rsquo;s why our retention and
          repeat-booking numbers sit well above the industry norm for boutique stays in Delhi.
        </p>
        <p className="editorial-body mt-5 text-xl">
          We&rsquo;ve also grown almost entirely through word of mouth. No paid ads, no OTA
          dependence &mdash; just a portfolio that people keep coming back to, and a team that picks up the
          phone.
        </p>

        <p className="font-accent italic text-2xl text-amber mt-12">&mdash; the Vyagram team</p>
      </section>

      {still && (
        <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
          <img src={still} alt="" className="w-full rounded-2xl object-cover shadow-card" style={{ aspectRatio: "16/7" }} />
        </section>
      )}

      <section className="bg-white/70 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <p className="vyg-eyebrow">By the numbers</p>
          <h2 className="mt-2 font-display text-3xl">Quietly consistent.</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            {[
              { n: `${propCount}+`, l: "Experience-led stays across Delhi & Gurugram" },
              { n: "4.95★", l: "Average rating across our portfolio" },
              { n: "2500+", l: "Guests hosted — and the number that keep coming back" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-display text-6xl text-forest">{s.n}</p>
                <p className="mt-2 text-noir/70">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FindYourStayCTA
        eyebrow="Ready to stay with us?"
        title="Pick your home in Delhi or Gurugram"
        subtitle="Ten quiet, design-led stays — every one hosted by us, no middleman."
      />
    </div>
  );
}
